import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  Desktop,
  Mobile
} from "@unified-checkout/design-system/icons/index";

import { COUNTRIES_INFO } from "@app/dashboard/constants/countries";
import {
  INITIAL_STATE as CUSTOMIZATION_INITIAL_STATE
  , useCheckoutCustomizationStore
} from "@app/dashboard/store/checkout-customization";
import type { TNavTabs } from "@app/dashboard/types/innerLayout/checkout_customization";
import { getCheckoutUrl } from "@app/dashboard/utils/serverUrls";
import { getCountryFromDomain } from "@app/dashboard/utils/getCountryFromDomain";
import { CHECKOUT_ORDER_DETAILS, filterToMatchStructure } from "../utils";
import BrowserToolBar from "../assets/images/BrowserToolBar.png";
import styles from "./preview.module.scss";


export const Preview = ({ activeTab }: { activeTab: TNavTabs }) => { 
  const { t } = useTranslation();
  const iframeRef = useRef<any>();

  const serverUrl = getCheckoutUrl();
  const iframeUrl = `${serverUrl}/?previewMode=true`;

  const {
    isFetched,
  } = useCheckoutCustomizationStore();
  const customizationData = useCheckoutCustomizationStore.getState().customizationData
  const businessLogo = useCheckoutCustomizationStore.getState().businessLogo

  const [isMobilePreview, setIsMobilePreview] = useState(false);
  const PREVIEW_MODE = isMobilePreview ? 'mobile' : 'desktop';

  const country = getCountryFromDomain();
  const currency = COUNTRIES_INFO[country].currency;


  const postMessageToCheckout = () => {
    // if (!iframeRef?.current?.contentWindow) return;

    const filteredCustomizationData = filterToMatchStructure(CUSTOMIZATION_INITIAL_STATE, customizationData);

    iframeRef.current.contentWindow.postMessage(
      {
        type: 'checkout-customizations',
        payload: {
          ...filteredCustomizationData,
          activeTab,
          previewMood: PREVIEW_MODE,
          merchant_logo: businessLogo.path,
          order_details: {
            currency: currency,
            ...CHECKOUT_ORDER_DETAILS
          },
        },
      },
      serverUrl
    );
  };

  useEffect(() => {
    isFetched && postMessageToCheckout();
  }, [
    activeTab,
    isMobilePreview,
    customizationData,
    businessLogo,
    isFetched
  ]);

  // Re-send the current customizations when the checkout iframe announces it is
  // ready ('checkout-ready'). The first send (on iframe onLoad / initial render)
  // can arrive before the checkout's listener is mounted and be lost. Validate
  // the sender is our own preview iframe AND the checkout origin before acting.
  const postRef = useRef(postMessageToCheckout);
  postRef.current = postMessageToCheckout;
  useEffect(() => {
    const onCheckoutReady = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!serverUrl || event.origin !== serverUrl) return;
      if (event.data?.type === 'checkout-ready') postRef.current();
    };
    window.addEventListener('message', onCheckoutReady);
    return () => window.removeEventListener('message', onCheckoutReady);
  }, [serverUrl]);

  return (
    <div className={styles["preview-wrapper"]}>

      <div className={styles["preview-wrapper__header"]}>

        <div className={styles["preview-wrapper__header__text-box"]}>
          <div className={styles["preview-wrapper__header__text-box__title"]}>
            {t('innerLayout.checkoutCustomizationPage.preview.title')}
          </div>
          <div className={styles["preview-wrapper__header__text-box__desc"]}>
            {t('innerLayout.checkoutCustomizationPage.preview.desc')}
          </div>
        </div>

        <div className={styles["preview-wrapper__header__views-toggler"]}>
          <div
            className={styles["preview-wrapper__header__views-toggler__icon"]}
            data-checkout-custom-preview-icon-selected={isMobilePreview === true}
            onClick={() => setIsMobilePreview(true)}
          >
            {Mobile}
          </div>
          <div
            className={styles["preview-wrapper__header__views-toggler__icon"]}
            data-checkout-custom-preview-icon-selected={isMobilePreview === false}
            onClick={() => setIsMobilePreview(false)}
          >
            {Desktop}
          </div>
        </div>

      </div>

      <div
        className={styles["preview-wrapper__preview"]}
        data-checkout-custom-preview-viewport-selected={isMobilePreview ? 'mobile' : 'desktop'}
      >
        {<img src={BrowserToolBar} alt="toolbar" />}
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          title={t('innerLayout.checkoutCustomizationPage.preview.title')}
          onLoad={postMessageToCheckout}
          allow="payment *"
        ></iframe>
      </div>
      <div className={styles["preview-wrapper__hint"]}>
        {t('innerLayout.checkoutCustomizationPage.preview.hint')}
      </div>
    </div>
  )
}